import { isMobile, isWeMeetApp } from '@tencent/web-base';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { DEFAULT_LIST, ShareListId } from '@tencent/calendar-web-component-mobile/es/Share';

export enum ShareThirdAppEnum {
  qq = 1,
  wx = 2,
  wework = 3,
  whatsapp = 8,
  wxTimeline = 10,
  xiaohongshu = 15,
  weibo = 16,
  mailMaster = 17,
}

// 对应app接口的key, 方便过滤
const appKeyOptions: Record<ShareListId, ShareThirdAppEnum | undefined> = {
  [ShareListId.wx]: ShareThirdAppEnum.wx,
  [ShareListId.wxMoment]: ShareThirdAppEnum.wx,
  [ShareListId.wework]: ShareThirdAppEnum.wework,
  [ShareListId.qq]: ShareThirdAppEnum.qq,
  [ShareListId.copy]: undefined,
};

const defaultShareList = [
  {
    icon: 'wechart',
    name: '微信',
    id: 'wx',
    appKey: ShareThirdAppEnum.wx,
  },
  {
    icon: 'friends',
    name: '朋友圈',
    id: 'wx_moment',
    appKey: ShareThirdAppEnum.wx,
  },
  {
    icon: 'enterprise',
    name: '企业微信',
    id: 'wework',
    appKey: ShareThirdAppEnum.wework,
  },
  {
    icon: 'qq',
    name: 'QQ',
    id: 'qq',
    appKey: ShareThirdAppEnum.qq,
  },
  {
    icon: 'download',
    name: '保存到相册',
    id: 'image',
  },
];

const useShowShare = () => {
  const [showShare, setShowShare] = useState(false);
  const [thirdAppList, setThirdAppList] = useState<number[]>([]);
  const [shareList, setShareList] = useState<typeof defaultShareList>([]);

  const hasUsualShare = (arr: number[]) => {
    const flag = arr.find(
      (item) =>
        item === ShareThirdAppEnum.qq ||
        item === ShareThirdAppEnum.wx ||
        item === ShareThirdAppEnum.wework,
    );

    return Boolean(flag);
  };

  // 获取能分享的已安装的app
  const getShareThirdAppList = useCallback(async () => {
    if (typeof window === 'undefined' || typeof wemeet === 'undefined') return [];
    try {
      const data: any = await wemeet.ui.getShareThirdAppList();
      const channelList = data?.channel_list;
      if (Array.isArray(channelList)) {
        return channelList;
      }
      return [];
    } catch (err) {
      console.log(`getChannelList error: ${err}`);
      window.nextAegis.infoAll(`getShareThirdAppList error: ${err}`);
      return [];
    }
  }, []);

  // 根据是否安装app来过滤展示的分享列表
  const shareComponentList = useMemo(() => {
    const filterList = DEFAULT_LIST.filter((listItem) => {
      const { id } = listItem;
      const appKey = appKeyOptions[id];
      if (appKey === undefined) return true; // 没有key的默认显示
      const hasApp = thirdAppList.includes(appKey);
      return hasApp;
    });
    return filterList;
  }, [thirdAppList]);

  // 在app内嵌页中同时没有qq、微信、企业微信时，屏蔽分享
  const handleShareShow = async () => {
    const appList = await getShareThirdAppList();
    setThirdAppList(appList);
    if (hasUsualShare(appList)) {
      setShowShare(true);
    } else {
      setShowShare(false);
    }
  };

  // 筛选默认列表，未安装的app，屏蔽分享按钮
  useEffect(() => {
    const list = defaultShareList.filter((shareItem) => {
      const { appKey } = shareItem;
      if (appKey === undefined) return true; // 不检查的直接显示
      const hasApp = thirdAppList.includes(appKey);
      return hasApp;
    });
    setShareList(list);
  }, [thirdAppList]);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      typeof wemeet !== 'undefined' &&
      isWeMeetApp() &&
      isMobile()
    ) {
      handleShareShow();
    } else {
      setShowShare(true);
    }
  }, []);

  return {
    showShare,
    thirdAppList,
    getShareThirdAppList,
    shareList,
    shareComponentList,
  };
};

export default useShowShare;
